import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Alert,
  Typography,
  Tooltip,
} from "@mui/material";
import NotificationImportantRoundedIcon from "@mui/icons-material/NotificationImportantRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import GlassCard from "../Cards/GlassCard";
import CommonDataGrid from "./CommonDataGrid";
import axiosInstance from "../../services/axiosInstance";

const formatDateTime = (iso) => {
  if (!iso) return "No recent activity";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "No recent activity";
  }
};

const csvEscape = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const LeetCodeAtRiskSection = ({ batchId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [weeks, setWeeks] = useState(2);

  useEffect(() => {
    if (!batchId) return;
    let active = true;

    setLoading(true);
    setError("");

    axiosInstance
      .get("/analytics/leetcode/at-risk", {
        params: { batchId, weeks: 2 },
      })
      .then(({ data }) => {
        if (!active) return;
        setWeeks(Number(data?.weeks) || 2);
        const list = Array.isArray(data?.students) ? data.students : [];
        setRows(
          list.map((item, idx) => ({
            id: item.student_id || idx,
            name: item.name || "Unknown",
            rollNumber: item.rollNumber || "—",
            problemsSolved: item.problemsSolved || 0,
            lastActivityDate: item.lastActivityDate || null,
          }))
        );
      })
      .catch((err) => {
        if (!active) return;
        setRows([]);
        setError(err?.response?.data?.message || "Failed to load at-risk students.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [batchId]);

  const columns = useMemo(
    () => [
      { field: "name", headerName: "Name", minWidth: 200, flex: 1.2 },
      { field: "rollNumber", headerName: "Roll Number", minWidth: 140, flex: 0.8 },
      {
        field: "problemsSolved",
        headerName: "Problems Solved",
        minWidth: 140,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "lastActivityDate",
        headerName: "Last Activity Date",
        minWidth: 220,
        flex: 1.1,
        renderCell: ({ value }) => (
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(value)}
          </Typography>
        ),
      },
    ],
    []
  );

  const handleExport = () => {
    const header = ["Name", "Roll Number", "Problems Solved", "Last Activity Date"];
    const body = rows.map((r) => [
      r.name,
      r.rollNumber,
      r.problemsSolved,
      formatDateTime(r.lastActivityDate),
    ]);
    const csv = [header, ...body].map((line) => line.map(csvEscape).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `at-risk-students-${batchId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <GlassCard hoverable={false} sx={{ borderRadius: 3, mt: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          <Box>
            {/* <Typography variant="subtitle1" fontWeight={700}>
              At Risk Students
            </Typography> */}
            <Typography variant="caption" color="text.secondary">
              Students with 0 or ≤ 2 solved problems in the last {weeks} weeks
            </Typography>
          </Box>
          <Tooltip title="Export at-risk list for outreach or notifications" arrow>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                endIcon={<NotificationImportantRoundedIcon />}
                onClick={handleExport}
                disabled={rows.length === 0}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
              >
                Notify / Export
              </Button>
            </span>
          </Tooltip>
        </Box>

        {loading && (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={30} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && rows.length === 0 && (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            No at-risk students found in the selected window.
          </Alert>
        )}

        {!loading && !error && rows.length > 0 && (
          <Box sx={{ height: 380 }}>
            <CommonDataGrid
              firstColumnField="name"
              rows={rows}
              columns={columns}
              defaultPageSize={10}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              initialState={{
                sorting: { sortModel: [{ field: "problemsSolved", sort: "asc" }] },
              }}
            />
          </Box>
        )}
      </CardContent>
    </GlassCard>
  );
};

export default LeetCodeAtRiskSection;
